/**
 * =============================================================================
 * Página: ActivitiesList
 * Versão: 1.0.0 (Fase 3 — itens 3.4, 3.5, 3.6, 3.7 e 3.9 do Playbook de Vendas)
 *
 * Listagem produtiva unificada de atividades (avulsas + cadência) com:
 *   - Header compacto + 4 KPIs (pendentes hoje, atrasadas, concluídas, taxa)
 *   - Filtros principais inline (mês, responsável, status quick chips, busca)
 *   - Sheet "Mais filtros" para tipo, origem, pipeline/fase, playbook
 *   - Tabela desktop com 8 colunas; cards no mobile
 *   - Footer com paginação server-side
 *
 * Toda leitura via RPCs `SECURITY DEFINER`:
 *   - list_user_activities_paginated
 *   - get_activities_kpis
 * =============================================================================
 */
import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { startOfMonth, endOfMonth, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  ListChecks,
  Search,
  Filter,
  RefreshCw,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Phone,
  Mail,
  MessageSquare,
  Calendar as CalendarIcon,
  CircleDot,
  SkipForward,
  Briefcase,
  Zap,
  Building2,
  Layers,
  FolderTree,
  Package,
} from 'lucide-react';
import { PaginationFooter } from '@/components/ui/pagination-footer';
// v2.1.0 — Tabela extraída para subcomponente reutilizável (Dashboard + Listagem)
import {
  ActivitiesTable,
  isActivityActionable,
  canEditActivityRow,
} from '@/components/activities/list/ActivitiesTable';
// v1.13.0 — Edição de atividade a partir da própria listagem (paridade com aba Cadência)
import { ActivityEditSheet } from '@/components/crm/deals/modal/components/ActivityEditSheet';
import {
  ManualCadenceStepDialog,
  type ManualStepDraft,
} from '@/components/activities/ManualCadenceStepDialog';

import {
  ListPageLayout,
  ListPageHeader,
  ListPageKpis,
  KpiCard,
  ListPageFiltersBar,
  LIST_FILTERS_INPUT_CLASS,
  ListPageContent,
  ListPageFooter,
  LIST_FILTERS_SELECT_TRIGGER_CLASS,
} from '@/components/layout/list-page';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
// v1.2.0 — Filtros padronizados reusados de "Todos os negócios"
import { CrmSellerFilter } from '@/components/crm/filters/CrmSellerFilter';
import { CrmWorkspaceFilter } from '@/components/crm/filters/CrmWorkspaceFilter';
import { CrmDealPhaseFilter } from '@/components/crm/filters/CrmDealPhaseFilter';
import { CrmDealSourceFilter } from '@/components/crm/filters/CrmDealSourceFilter';
import { CategoryFilterSelect } from '@/components/crm/CategoryFilterSelect';
import { useCrmWorkspacesHierarchy } from '@/hooks/useCrmWorkspacesHierarchy';
import { useDealFilterOptions } from '@/hooks/useDealFilterOptions';
import { MonthNavigator } from '@/components/financial/MonthNavigator';
import {
  calculatePeriodDates,
  type PeriodPreset,
} from '@/hooks/useCrmOverviewFilters';
import { ActivitiesBulkEditBar } from '@/components/activities/ActivitiesBulkEditBar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { DealDetailsModalPremium } from '@/components/crm/deals/modal/DealDetailsModalPremium';
// v1.4.0 — Modal de execução/visualização de atividade (cadência ou avulsa)
// v1.5.0 — Removido ActivityDetailsModalPremium desta tela: o clique no título
// agora abre o DealDetailsModalPremium na aba correta (cadência ou resumo).
import { PhaseProgressCell } from '@/components/crm/deals/PhaseProgressCell';
// v2.0.0 — Badge canônico de status do negócio (mesmo usado em "Todos os negócios")
import { DealStatusBadge } from '@/components/crm/DealStatusBadge';
import { formatCurrency } from '@/lib/currency-utils';
import { getDeadlineInfo, DEADLINE_PILL_STYLES } from '@/lib/deadline-utils';
import {
  useActivitiesPaginated,
  useActivitiesKpis,
  useActivitiesTypeCounts,
  type ActivityRow,
  type ActivityStatus,
  type ActivityOrigin,
  type ActivitiesFilters,
} from '@/hooks/useActivitiesPaginated';
import { useActivitiesRealtime } from '@/hooks/useActivitiesRealtime';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { ActivitiesTypeTabs } from '@/components/activities/ActivitiesTypeTabs';

// =============================================================================
// Constantes auxiliares (UI)
// =============================================================================

/** Quick filters de status (chips na barra principal). */
const STATUS_CHIPS: Array<{ key: ActivityStatus; label: string; tone: string }> = [
  { key: 'pending', label: 'Pendentes', tone: 'bg-blue-50 text-blue-700 border-blue-200' },
  { key: 'overdue', label: 'Atrasadas', tone: 'bg-red-50 text-red-700 border-red-200' },
  { key: 'completed', label: 'Concluídas', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
];

const logger = createLogger('ActivitiesList');

// Tipos de atividade visíveis na listagem (notes/system existem só no Feed).
// Fonte única de verdade compartilhada entre filtros, KPIs e contadores das abas.
const VISIBLE_ACTIVITY_TYPES = ['call', 'email', 'whatsapp', 'meeting', 'task'] as const;


// =============================================================================
// Componente principal
// =============================================================================
export default function ActivitiesList() {
  const { currentTenant } = useAuth();

  // Item 3.10 — assina realtime de crm_deal_activities + crm_deal_cadence_steps
  // e invalida o cache do TanStack Query para esta página.
  useActivitiesRealtime(currentTenant?.id ?? null);

  // ---------------- Estado dos filtros ---------------------------------------
  const [currentMonth, setCurrentMonth] = useState<Date | null>(new Date());
  // v1.9.0 (2026-07-24) — Preset ativo do MonthNavigator. Quando não é
  // 'month'/null, o range vem do preset (ver `period` memo abaixo) e as setas
  // mensais somem no MonthNavigator.
  const [activePreset, setActivePreset] = useState<PeriodPreset | null>('month');
  const [customRange, setCustomRange] = useState<{ startDate: Date; endDate: Date } | null>(null);
  // v1.2.0 — renomeado para "vendedor" (UI), continua mapeado para assigneeIds da RPC
  const [sellerId, setSellerId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<ActivityStatus | null>(null);
  const [search, setSearch] = useState('');
  const [origin, setOrigin] = useState<ActivityOrigin | 'all'>('all');
  const [activityType, setActivityType] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // v1.6.0 — Filtro rápido via clique nos KPIs (toggle)
  // Valores possíveis: 'pending_today' | 'overdue' | 'completed' | 'skipped' | null
  type KpiQuickKey = 'pending_in_period' | 'pending_today' | 'overdue' | 'completed' | 'skipped' | null;
  const [activeKpi, setActiveKpi] = useState<KpiQuickKey>(null);

  // v1.6.0 — Inclusão de atividades concluídas/puladas (default: oculta)
  const [includeCompleted, setIncludeCompleted] = useState<boolean>(false);

  // v1.2.0 — Filtros novos espelhando "Todos os negócios"
  const [workspaceFilter, setWorkspaceFilter] = useState<string | null>(null);
  const [pipelineFilter, setPipelineFilter] = useState<string>('all');
  const [phaseFilter, setPhaseFilter] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [productFilter, setProductFilter] = useState<string>('all');
  // v2.0.0 (2026-07-11): Origem agora é multi-seleção
  const [sourceFilter, setSourceFilter] = useState<string[]>([]);
  // v1.7.0 — filtro "Realizado por" (executor real) no Sheet "Mais filtros"
  const [performedById, setPerformedById] = useState<string | null>(null);

  // v1.8.0 — Filtros por data de criação / conclusão (Sheet "Mais filtros").
  // Formato ISO 'YYYY-MM-DD' aceito diretamente pela RPC.
  const [createdStart, setCreatedStart] = useState<string>('');
  const [createdEnd, setCreatedEnd] = useState<string>('');
  const [completedStart, setCompletedStart] = useState<string>('');
  const [completedEnd, setCompletedEnd] = useState<string>('');


  // Hierarquia de workspaces para o select inline de Pipeline
  const { workspaces } = useCrmWorkspacesHierarchy();
  const { products } = useDealFilterOptions();

  // Pipelines disponíveis dependem do workspace selecionado
  const filteredPipelines = useMemo(() => {
    if (!workspaceFilter) return workspaces.flatMap((w) => w.pipelines);
    const w = workspaces.find((x) => x.id === workspaceFilter);
    return w?.pipelines || [];
  }, [workspaces, workspaceFilter]);

  // Resets em cascata (workspace -> pipeline+fase; pipeline -> fase)
  const handleWorkspaceChange = (id: string | null) => {
    setWorkspaceFilter(id);
    setPipelineFilter('all');
    setPhaseFilter(null);
  };
  const handlePipelineChange = (id: string) => {
    setPipelineFilter(id);
    setPhaseFilter(null);
  };

  // v2.0.0 — Edição em massa: checkboxes ficam sempre visíveis; a barra
  // de ações aparece automaticamente quando há seleção.
  // v1.1.0 — modal de detalhes do negócio aberto direto da listagem
  const [viewDealId, setViewDealId] = useState<string | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  // v1.7.0 — pré-seleciona aba/step ao abrir vindo de uma linha de cadência
  const [viewInitialTab, setViewInitialTab] = useState<'summary' | 'cadence'>('summary');
  const [viewInitialStepId, setViewInitialStepId] = useState<string | null>(null);

  // v1.4.0 — Modal de detalhes da ATIVIDADE (foco em execução do roteiro)
  // v1.5.0 — Estado do modal individual de atividade removido (substituído pelo DealDetailsModalPremium)

  /**
   * Abre o DealDetailsModalPremium localmente (sem navegar para a lista de negócios).
   * Aceita overrides opcionais para abrir já em uma aba específica e com um step
   * de cadência pré-selecionado (ganho de tempo ao clicar no negócio na listagem).
   */
  const openDealModal = (
    dealId: string,
    opts?: { initialTab?: 'summary' | 'cadence'; initialStepId?: string | null },
  ) => {
    setViewDealId(dealId);
    setViewInitialTab(opts?.initialTab ?? 'summary');
    setViewInitialStepId(opts?.initialStepId ?? null);
    setViewModalOpen(true);
  };

  /**
   * v1.5.0 — Clique no título abre o DealDetailsModalPremium replicando a regra
   * da coluna Negócio:
   *   - origem 'cadence'   → aba Cadência com o step pré-selecionado
   *   - origem 'standalone' (avulsa) → aba Resumo (a atividade aparece na timeline)
   */
  const openActivityFromRow = (row: ActivityRow) => {
    openDealModal(
      row.deal_id,
      row.origin === 'cadence'
        ? { initialTab: 'cadence', initialStepId: row.activity_id }
        : { initialTab: 'summary' },
    );
  };

  // ==========================================================================
  // v1.13.0 — Edição in-line na listagem (paridade com aba Cadência do modal)
  // --------------------------------------------------------------------------
  // Atividades avulsas usam o ActivityEditSheet (RPC update_deal_activity).
  // Steps manuais de cadência usam o ManualCadenceStepDialog (RPC
  // update_manual_cadence_step) — o mesmo dialog acionado no botão "Editar
  // atividade" da aba Cadência do modal de Negócio.
  // ==========================================================================
  const [editStandalone, setEditStandalone] = useState<{
    id: string;
    activity_type: string;
    title: string;
    description: string | null;
    scheduled_at: string | null;
  } | null>(null);
  const [editManualStep, setEditManualStep] = useState<
    (ManualStepDraft & { deal_id: string | null }) | null
  >(null);

  /**
   * Roteia o clique de "Editar" da linha para o modal correto e, no caso de
   * atividade avulsa, faz um fetch pontual da `description` (não vem no RPC de
   * listagem) para não zerar o campo ao salvar.
   */
  const handleEditRow = async (row: ActivityRow) => {
    if (!canEditActivityRow(row)) return;

    if (row.origin === 'standalone') {
      // Busca cirúrgica da descrição (apenas 1 coluna) — respeita RLS tenant.
      const { data, error } = await supabase
        .from('crm_deal_activities')
        .select('description')
        .eq('id', row.activity_id)
        .maybeSingle();
      if (error) {
        toast.error('Não foi possível carregar a atividade para edição.');
        return;
      }
      setEditStandalone({
        id: row.activity_id,
        activity_type: row.activity_type,
        title: row.title,
        description: (data?.description as string | null) ?? null,
        scheduled_at: row.due_at,
      });
      return;
    }

    if (row.origin === 'cadence' && row.is_manual) {
      // A description_html já é carregada dentro do dialog via updateManualStep,
      // porém o dialog atual não faz refetch — precisamos fornecer o valor
      // atual. Buscamos junto ao step da tabela crm_deal_cadence_steps.
      const { data, error } = await supabase
        .from('crm_deal_cadence_steps')
        .select('description_html, phase_id')
        .eq('id', row.activity_id)
        .maybeSingle();
      if (error) {
        toast.error('Não foi possível carregar o step para edição.');
        return;
      }
      setEditManualStep({
        step_id: row.activity_id,
        activity_type: row.activity_type,
        title: row.title,
        due_at: row.due_at,
        description_html: (data?.description_html as string | null) ?? null,
        phase_id: (data?.phase_id as string | null) ?? null,
        deal_id: row.deal_id,
      });
    }
  };

  // ---------------- Filtros consolidados -------------------------------------
  // Período: cobre o mês selecionado (ou aberto se null = sem filtro de mês).
  // Quando o KPI "Pendentes hoje" está ativo, restringimos a janela ao dia
  // corrente em America/Sao_Paulo (mesma TZ da RPC), garantindo que a listagem
  // bata com o contador do card.
  const period = useMemo(() => {
    if (activeKpi === 'pending_today') {
      // yyyy-MM-dd no fuso de Brasília, independente da TZ do navegador
      const todayBR = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Sao_Paulo',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(new Date());
      return { dateStart: todayBR, dateEnd: todayBR } as const;
    }
    // v1.9.0 — Preset personalizado tem precedência sobre a navegação mensal.
    if (activePreset === 'custom' && customRange) {
      return {
        dateStart: format(customRange.startDate, 'yyyy-MM-dd'),
        dateEnd: format(customRange.endDate, 'yyyy-MM-dd'),
      } as const;
    }
    if (activePreset && activePreset !== 'month') {
      const { startDate, endDate } = calculatePeriodDates(activePreset);
      return {
        dateStart: format(startDate, 'yyyy-MM-dd'),
        dateEnd: format(endDate, 'yyyy-MM-dd'),
      } as const;
    }
    if (!currentMonth) return { dateStart: null, dateEnd: null } as const;
    return {
      dateStart: format(startOfMonth(currentMonth), 'yyyy-MM-dd'),
      dateEnd: format(endOfMonth(currentMonth), 'yyyy-MM-dd'),
    } as const;
  }, [currentMonth, activeKpi, activePreset, customRange]);

  /**
   * v1.6.0 — Resolve o array de status enviado para a RPC combinando:
   *  1) KPI ativo (precedência máxima)
   *  2) `statusFilter` legado do Sheet
   *  3) Flag `includeCompleted` (oculta concluídas/puladas por padrão)
   */
  const resolvedStatus: ActivityStatus[] | null = useMemo(() => {
    if (activeKpi === 'pending_in_period') return ['pending', 'in_progress'];
    // v1.7.0 — Inclui 'overdue': o KPI conta tudo que vence hoje e não foi
    // concluído; a listagem normaliza due_at < NOW() para 'overdue'.
    if (activeKpi === 'pending_today') return ['pending', 'in_progress', 'overdue'];

    if (activeKpi === 'overdue') return ['overdue'];
    if (activeKpi === 'completed') return ['completed'];
    if (activeKpi === 'skipped') return ['skipped'];
    if (statusFilter) return [statusFilter];
    if (!includeCompleted) {
      // Oculta concluídas e puladas por padrão (mesmo padrão da listagem de Tarefas)
      return ['pending', 'in_progress', 'overdue'];
    }
    return null;
  }, [activeKpi, statusFilter, includeCompleted]);

  const filters: ActivitiesFilters = useMemo(
    () => ({
      dateStart: period.dateStart,
      dateEnd: period.dateEnd,
      assigneeIds: sellerId ? [sellerId] : null,
      // Notas existem apenas no Feed de Atividades; aqui exibimos apenas tipos operacionais.
      activityTypes: activityType === 'all'
        ? [...VISIBLE_ACTIVITY_TYPES]
        : [activityType],
      status: resolvedStatus,
      origin: origin === 'all' ? null : origin,
      search: search.trim() || null,
      // v1.2.0 — novos filtros enviados como arrays single-element
      workspaceIds: workspaceFilter ? [workspaceFilter] : null,
      pipelineIds: pipelineFilter !== 'all' ? [pipelineFilter] : null,
      phaseIds: phaseFilter ? [phaseFilter] : null,
      categoryIds: categoryFilter ? [categoryFilter] : null,
      productIds: productFilter !== 'all' ? [productFilter] : null,
      sources: sourceFilter.length > 0 ? sourceFilter : null,
      // v1.7.0 — filtro "Realizado por" (executor real)
      performedByIds: performedById ? [performedById] : null,
      // v1.8.0 — filtros aditivos por data de criação / conclusão
      createdStart: createdStart || null,
      createdEnd: createdEnd || null,
      completedStart: completedStart || null,
      completedEnd: completedEnd || null,
    }),
    [
      period, sellerId, activityType, resolvedStatus, origin, search,
      workspaceFilter, pipelineFilter, phaseFilter,
      categoryFilter, productFilter, sourceFilter, performedById,
      createdStart, createdEnd, completedStart, completedEnd,
    ],
  );


  // v1.7.0 — KPIs usam EXATAMENTE os mesmos filtros da Lista (incluindo status).
  // Assim o card "Total no Período" reflete o mesmo total exibido no footer da
  // tabela, eliminando divergências entre Card / Aba "Todos" / footer.
  const kpiFilters: ActivitiesFilters = filters;

  // Contadores das abas por TIPO usam os mesmos filtros da tabela, mas
  // restringidos aos tipos visíveis (sem 'note'/'system'), para que a aba
  // "Todos" coincida com o Card Total e com o footer.
  const typeCountFilters: ActivitiesFilters = useMemo(
    () => ({ ...filters, activityTypes: [...VISIBLE_ACTIVITY_TYPES] }),
    [filters],
  );

  // ---------------- Hooks de dados -------------------------------------------
  const { data: kpis, isLoading: kpisLoading } = useActivitiesKpis(kpiFilters);
  const { data: typeCounts, isLoading: typeCountsLoading } = useActivitiesTypeCounts(typeCountFilters);
  const {
    data,
    isLoading,
    isFetching,
    refetch,
  } = useActivitiesPaginated({ ...filters, page, pageSize });

  // Reset de página ao mudar filtros (exceto a própria paginação)
  // Heurística simples: dependência do JSON dos filtros
  const filtersKey = JSON.stringify(filters);
  useMemo(() => setPage(1), [filtersKey]);

  // v1.6.0 — Toggle de KPI clicável (com side effects necessários)
  const toggleKpi = (key: Exclude<KpiQuickKey, null>) => {
    setActiveKpi((cur) => {
      const next = cur === key ? null : key;
      // Concluídas / Puladas exigem o flag ligado para aparecerem na lista
      if (next === 'completed' || next === 'skipped') {
        setIncludeCompleted(true);
      }
      return next;
    });
    // Limpa filtro legado de status do Sheet para evitar conflito
    setStatusFilter(null);
  };

  // Conta filtros ativos no botão "Mais filtros"
  const advancedActiveCount =
    (statusFilter ? 1 : 0) +
    (activityType !== 'all' ? 1 : 0) +
    (categoryFilter ? 1 : 0) +
    (productFilter !== 'all' ? 1 : 0) +
    (sourceFilter.length > 0 ? 1 : 0) +
    (performedById ? 1 : 0) +
    ((createdStart || createdEnd) ? 1 : 0) +
    ((completedStart || completedEnd) ? 1 : 0);


  // ---------------- Seleção / Ações em massa (item 3.8) ----------------------
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Record<string, { origin: ActivityOrigin; id: string }>>({});
  const [bulkLoading, setBulkLoading] = useState(false);
  // v2.1.0 — confirmação para ação destrutiva "Excluir" em massa
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const selectedKeys = Object.keys(selected);
  const selectedCount = selectedKeys.length;
  const rows = data?.rows ?? [];
  // Apenas atividades em status "ativos" podem ser selecionadas para ações em massa.
  // Status finais (pulada/concluida/cancelada) são imutáveis e não aceitam Concluir/Reagendar/Atribuir/Pular.
  const actionableRows = rows.filter((r) => isActivityActionable(r.status));
  const allSelected =
    actionableRows.length > 0 &&
    actionableRows.every((r) => selected[`${r.origin}-${r.activity_id}`]);

  const toggleRow = (row: ActivityRow) => {
    // Defesa em profundidade: ignora toggle em atividades já finalizadas
    if (!isActivityActionable(row.status)) return;
    const key = `${row.origin}-${row.activity_id}`;
    setSelected((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = { origin: row.origin, id: row.activity_id };
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      // remove apenas as da página atual (somente acionáveis)
      setSelected((prev) => {
        const next = { ...prev };
        actionableRows.forEach((r) => delete next[`${r.origin}-${r.activity_id}`]);
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = { ...prev };
        actionableRows.forEach((r) => {
          next[`${r.origin}-${r.activity_id}`] = { origin: r.origin, id: r.activity_id };
        });
        return next;
      });
    }
  };

  const clearSelection = () => setSelected({});

  // Executa ação em massa via RPC bulk_activities_action
  // action: 'complete' | 'skip' | 'reschedule' | 'assign'
  const runBulkAction = async (
    action: 'complete' | 'skip' | 'reschedule' | 'assign' | 'delete',
    extra?: { dueAt?: string; assigneeId?: string },
  ) => {
    if (selectedCount === 0) return;
    setBulkLoading(true);
    try {
      const items = Object.values(selected).map((s) => ({ origin: s.origin, id: s.id }));
      const { data: result, error } = await supabase.rpc('bulk_activities_action' as never, {
        p_items: items as never,
        p_action: action,
        p_tenant_hint: currentTenant?.id ?? null,
        p_due_at: extra?.dueAt ?? null,
        p_assignee_id: extra?.assigneeId ?? null,
      } as never);
      if (error) throw error;

      const r = result as { processed: number; skipped: number; errors: unknown[]; total: number };
      const errCount = Array.isArray(r?.errors) ? r.errors.length : 0;

      const labelMap: Record<typeof action, string> = {
        complete: 'concluída(s)',
        skip: 'pulado(s)',
        reschedule: 'reagendada(s)',
        assign: 'atribuída(s)',
        delete: 'excluída(s)',
      };

      if (errCount > 0) {
        toast.warning(
          `${r.processed}/${r.total} processadas · ${errCount} com erro${r.skipped ? ` · ${r.skipped} ignoradas` : ''}`,
        );
        logger.warn('bulk action partial', { result: r });
      } else {
        toast.success(
          `${r.processed} atividade(s) ${labelMap[action]}${r.skipped ? ` · ${r.skipped} ignoradas` : ''}`,
        );
      }

      clearSelection();
      queryClient.invalidateQueries({ queryKey: ['activities-list'] });
      queryClient.invalidateQueries({ queryKey: ['activities-paginated'] });
      queryClient.invalidateQueries({ queryKey: ['activities-kpis'] });
      queryClient.invalidateQueries({ queryKey: ['activities-type-counts'] });
    } catch (err) {
      logger.error('bulk action failed', err);
      toast.error('Falha ao executar ação em massa');
    } finally {
      setBulkLoading(false);
    }
  };

  // ---------------- Render ---------------------------------------------------
  return (
    <ListPageLayout>
      <ListPageHeader
        title="Atividades"
        description="Sua agenda unificada — atividades avulsas e passos de cadência."
        center={
          <MonthNavigator
            currentMonth={currentMonth ?? new Date()}
            onChange={(d) => {
              // Ao usar as setas mensais, sair de qualquer preset não-mensal.
              setActivePreset('month');
              setCustomRange(null);
              setCurrentMonth(d);
            }}
            onClear={() => {
              // Limpa mês e preset — mostra todos os períodos.
              setCurrentMonth(null);
              setActivePreset(null);
              setCustomRange(null);
            }}
            activePreset={activePreset}
            customRange={customRange}
            onPresetChange={(preset) => {
              setActivePreset(preset);
              if (preset === 'month') {
                setCurrentMonth(new Date());
                setCustomRange(null);
              } else {
                setCustomRange(null);
              }
            }}
            onCustomChange={(startDate, endDate) => {
              setActivePreset('custom');
              setCustomRange({ startDate, endDate });
            }}
          />
        }
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
            aria-label="Atualizar"
          >
            <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
          </Button>
        }
      />

      {/* ---------------- KPIs (clicáveis) ------------------------------- */}
      <ListPageKpis columns={6}>
        {/* Total no Período — primeiro para visão geral imediata */}
        <KpiCard
          color="cyan"
          icon={ListChecks}
          label="Total no Período"
          value={kpisLoading ? '—' : kpis?.total_in_period ?? 0}
          active={activeKpi === null && includeCompleted === false ? false : false}
          onClick={() => {
            // Limpa quaisquer filtros rápidos para mostrar tudo do período
            setActiveKpi(null);
            setStatusFilter(null);
            setIncludeCompleted(true);
          }}
        />
        {/* Pendente no Período — todas as pendentes/em andamento do período filtrado */}
        <KpiCard
          color="purple"
          icon={Clock}
          label="Pendente no Período"
          value={kpisLoading ? '—' : kpis?.pending_in_period ?? 0}
          active={activeKpi === 'pending_in_period'}
          onClick={() => toggleKpi('pending_in_period')}
        />
        <KpiCard
          color="blue"
          icon={Clock}
          label="Pendentes hoje"
          value={kpisLoading ? '—' : kpis?.pending_today ?? 0}
          active={activeKpi === 'pending_today'}
          onClick={() => toggleKpi('pending_today')}
        />
        <KpiCard
          color="red"
          icon={AlertTriangle}
          label="Atrasadas"
          value={kpisLoading ? '—' : kpis?.overdue ?? 0}
          active={activeKpi === 'overdue'}
          onClick={() => toggleKpi('overdue')}
        />
        <KpiCard
          color="green"
          icon={CheckCircle2}
          label="Concluídas no período"
          value={kpisLoading ? '—' : kpis?.completed_in_period ?? 0}
          active={activeKpi === 'completed'}
          onClick={() => toggleKpi('completed')}
        />
        <KpiCard
          color="orange"
          icon={SkipForward}
          label="Pulada"
          value={kpisLoading ? '—' : kpis?.skipped_in_period ?? 0}
          active={activeKpi === 'skipped'}
          onClick={() => toggleKpi('skipped')}
        />
      </ListPageKpis>

      {/* ---------------- Abas por TIPO de atividade (server-side) -------- */}
      <ActivitiesTypeTabs
        value={activityType}
        onChange={setActivityType}
        counts={typeCounts ?? {}}
        isLoading={typeCountsLoading}
      />

      {/* ---------------- Filtros principais ------------------------------ */}
      <ListPageFiltersBar isFetching={isFetching}>
        {/* Workspace */}
        <CrmWorkspaceFilter
          value={workspaceFilter}
          onChange={handleWorkspaceChange}
          className={LIST_FILTERS_INPUT_CLASS}
        />

        {/* Pipeline inline — agrupado por workspace, com bolinha de cor para distinguir pipelines homônimos */}
        {(() => {
          // Workspaces visíveis: todos quando "Todos workspaces", senão só o selecionado
          const visibleWorkspaces = workspaceFilter
            ? workspaces.filter((w) => w.id === workspaceFilter)
            : workspaces;
          // Workspace+pipeline atualmente selecionado (para o texto do trigger)
          const selectedWs = pipelineFilter !== 'all'
            ? workspaces.find((w) => w.pipelines.some((p) => p.id === pipelineFilter))
            : undefined;
          const selectedPipeline = selectedWs?.pipelines.find((p) => p.id === pipelineFilter);
          const fallbackDot = 'hsl(var(--muted-foreground))';
          return (
            <Select value={pipelineFilter} onValueChange={handlePipelineChange}>
              <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, 'sm:w-[180px]')}>
                <div className="flex items-center gap-1.5 min-w-0 flex-1">
                  <Layers className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0 text-muted-foreground" />
                  {selectedPipeline && (
                    <span
                      className="h-2 w-2 rounded-full shrink-0"
                      style={{ backgroundColor: selectedWs?.color || fallbackDot }}
                    />
                  )}
                  <span className="truncate min-w-0 flex-1 text-left">
                    {selectedPipeline
                      ? `${selectedWs?.name} · ${selectedPipeline.name}`
                      : 'Pipeline'}
                  </span>
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos pipelines</SelectItem>
                {visibleWorkspaces.map((ws) => (
                  <SelectGroup key={ws.id}>
                    <SelectLabel>{ws.name}</SelectLabel>
                    {ws.pipelines.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        <span className="flex items-center gap-2">
                          <span
                            className="h-2 w-2 rounded-full shrink-0"
                            style={{ backgroundColor: ws.color || fallbackDot }}
                          />
                          <span className="truncate">{p.name}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          );
        })()}


        {/* Fase (depende de pipeline) */}
        <CrmDealPhaseFilter
          pipelineId={pipelineFilter !== 'all' ? pipelineFilter : null}
          value={phaseFilter}
          onChange={setPhaseFilter}
          className={LIST_FILTERS_INPUT_CLASS}
        />

        {/* Vendedor */}
        <CrmSellerFilter value={sellerId} onChange={setSellerId} className={LIST_FILTERS_INPUT_CLASS} />

        {/* v1.6.0 — Origem da atividade (movido do Sheet para a barra principal) */}
        <Select
          value={origin}
          onValueChange={(v) => setOrigin(v as ActivityOrigin | 'all')}
        >
          <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, 'sm:w-[150px]')}>
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              <Zap className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate min-w-0 flex-1 text-left">
                {origin === 'all'
                  ? 'Origem'
                  : origin === 'standalone'
                    ? 'Avulsa'
                    : origin === 'booking'
                      ? 'Agendamento online'
                      : 'Cadência'}
              </span>
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as origens</SelectItem>
            <SelectItem value="standalone">Avulsa</SelectItem>
            <SelectItem value="cadence">Cadência (Playbook)</SelectItem>
            <SelectItem value="booking">Agendamento online</SelectItem>
          </SelectContent>
        </Select>

        {/* Busca */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar atividade ou negócio..."
            className={`${LIST_FILTERS_INPUT_CLASS} pl-8`}
          />
        </div>

        {/* v1.6.0 — Flag "Incluir concluídos" */}
        <label className="flex items-center gap-1.5 text-[11px] sm:text-xs cursor-pointer select-none whitespace-nowrap">
          <Checkbox
            checked={includeCompleted}
            onCheckedChange={(v) => setIncludeCompleted(!!v)}
            aria-label="Incluir concluídos"
          />
          <span className="text-muted-foreground hidden sm:inline">Incluir concluídos</span>
          <span className="text-muted-foreground sm:hidden">Concluídos</span>
        </label>

        {/* Botão "Mais filtros" abre Sheet */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowAdvanced(true)}
          className="h-6 px-2 text-[10px] gap-1.5 sm:h-8 sm:px-3 sm:text-xs rounded-full"
        >
          <Filter className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
          <span className="hidden sm:inline">Mais filtros</span>
          <span className="sm:hidden">Filtros</span>
          {advancedActiveCount > 0 && (
            <Badge variant="secondary" className="h-4 min-w-[16px] px-1 text-[10px] rounded-full bg-primary/15 text-primary border-0">
              {advancedActiveCount}
            </Badge>
          )}
        </Button>
      </ListPageFiltersBar>

      {/* ---------------- Barra de ações em massa (aparece ao selecionar) - */}
      {selectedCount > 0 && (() => {
        // v1.1.0 — Deriva a linha única selecionada para habilitar o botão "Editar"
        const singleRow =
          selectedCount === 1
            ? rows.find((r) => `${r.origin}-${r.activity_id}` === selectedKeys[0]) ?? null
            : null;
        return (
          <ActivitiesBulkEditBar
            selectedCount={selectedCount}
            loading={bulkLoading}
            onComplete={() => runBulkAction('complete')}
            onReschedule={(iso) => runBulkAction('reschedule', { dueAt: iso })}
            onAssign={(userId) => runBulkAction('assign', { assigneeId: userId })}
            onSkip={() => runBulkAction('skip')}
            onDelete={() => setConfirmDeleteOpen(true)}
            onEdit={singleRow ? () => handleEditRow(singleRow) : undefined}
            canEdit={!!singleRow && canEditActivityRow(singleRow)}
            onCancel={clearSelection}
          />
        );
      })()}

      {/* ---------------- Confirmação de exclusão em massa (v2.1.0) --------- */}
      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir atividades selecionadas?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação é permanente e não pode ser desfeita. Somente atividades
              avulsas serão excluídas — passos de cadência e agendamentos serão
              ignorados. A permissão será validada individualmente para cada
              atividade.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkLoading}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={bulkLoading}
              onClick={() => {
                setConfirmDeleteOpen(false);
                runBulkAction('delete');
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ---------------- Conteúdo (tabela / cards) ----------------------- */}
      <ListPageContent>
        <ActivitiesTable
          rows={data?.rows ?? []}
          isLoading={isLoading}
          bulkMode
          allSelected={allSelected}
          selected={selected}
          onToggleAll={toggleAll}
          onToggleRow={toggleRow}
          onOpenDeal={(row) =>
            openDealModal(
              row.deal_id,
              row.origin === 'cadence'
                ? { initialTab: 'cadence', initialStepId: row.activity_id }
                : undefined,
            )
          }
          onOpenActivity={(row) => openActivityFromRow(row)}
          onEditRow={handleEditRow}
        />
      </ListPageContent>

      {/* ---------------- Footer (paginação) ------------------------------ */}
      {data && data.total > 0 && (
        <ListPageFooter>
          <PaginationFooter
            totalItems={data.total}
            currentPage={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPage(1);
            }}
            pageSizeOptions={[25, 50, 75, 100]}
            borderClassName=""
            className="mt-0 pt-0"
          />
        </ListPageFooter>
      )}

      {/* ---------------- Sheet "Mais filtros" ---------------------------- */}
      <Sheet open={showAdvanced} onOpenChange={setShowAdvanced}>
        <SheetContent side="right" className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Mais filtros</SheetTitle>
            <SheetDescription>
              Refine a lista com filtros avançados.
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-4 py-4">
            {/* Status (chips) — movidos para o Sheet em v1.5.0 */}
            <div className="space-y-1.5">
              <Label className="text-xs">Status</Label>
              <div className="flex items-center gap-1 flex-wrap">
                {STATUS_CHIPS.map((chip) => {
                  const active = statusFilter === chip.key;
                  return (
                    <button
                      key={chip.key}
                      type="button"
                      onClick={() => setStatusFilter(active ? null : chip.key)}
                      className={cn(
                        'h-8 px-2.5 text-xs rounded-md border transition-colors',
                        active
                          ? chip.tone
                          : 'bg-background text-muted-foreground border-border hover:bg-muted',
                      )}
                    >
                      {chip.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* v1.7.0 — Realizado por (executor real da atividade) */}
            <div className="space-y-1.5">
              <Label className="text-xs">Realizado por</Label>
              <CrmSellerFilter value={performedById} onChange={setPerformedById} className="w-full" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5">Origem do negócio</Label>
              <CrmDealSourceFilter value={sourceFilter} onChange={setSourceFilter} className="w-full" />
            </div>

            {/* Categoria de produtos — v1.2.0 */}
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5">
                <FolderTree className="h-3 w-3" /> Categoria
              </Label>
              <CategoryFilterSelect
                value={categoryFilter}
                onChange={setCategoryFilter}
                placeholder="Todas"
                size="xs"
                className="w-full"
              />
            </div>

            {/* Produto — v1.2.0 */}
            {products.length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-xs flex items-center gap-1.5">
                  <Package className="h-3 w-3" /> Produto
                </Label>
                <Select
                  value={productFilter}
                  onValueChange={(v) => setProductFilter(v)}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {products.map((prod) => (
                      <SelectItem key={prod.id} value={prod.id}>{prod.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* v1.6.0 — Origem da atividade movida para a barra principal */}
            {/* v1.6.0 — Tipo de atividade migrou para as abas server-side acima */}

            {/* v1.8.0 — Data de criação da atividade */}
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5">
                <CalendarIcon className="h-3 w-3" /> Data de criação
              </Label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="date"
                  value={createdStart}
                  onChange={(e) => setCreatedStart(e.target.value)}
                  className="h-8 text-xs"
                  aria-label="Criada a partir de"
                />
                <Input
                  type="date"
                  value={createdEnd}
                  onChange={(e) => setCreatedEnd(e.target.value)}
                  className="h-8 text-xs"
                  aria-label="Criada até"
                />
              </div>
            </div>

            {/* v1.8.0 — Data de conclusão da atividade */}
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5">
                <CheckCircle2 className="h-3 w-3" /> Data de conclusão
              </Label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="date"
                  value={completedStart}
                  onChange={(e) => setCompletedStart(e.target.value)}
                  className="h-8 text-xs"
                  aria-label="Concluída a partir de"
                />
                <Input
                  type="date"
                  value={completedEnd}
                  onChange={(e) => setCompletedEnd(e.target.value)}
                  className="h-8 text-xs"
                  aria-label="Concluída até"
                />
              </div>
            </div>
          </div>


          <SheetFooter>
            <Button
              variant="outline"
              onClick={() => {
                setStatusFilter(null);
                setOrigin('all');
                setActivityType('all');
                setCategoryFilter(null);
                setProductFilter('all');
                setSourceFilter([]);
                setPerformedById(null);
                setCreatedStart('');
                setCreatedEnd('');
                setCompletedStart('');
                setCompletedEnd('');

              }}
            >
              Limpar
            </Button>
            <Button onClick={() => setShowAdvanced(false)}>Aplicar</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* v1.1.0 — Modal de detalhes do negócio aberto direto da listagem */}
      <DealDetailsModalPremium
        dealId={viewDealId}
        open={viewModalOpen}
        onOpenChange={setViewModalOpen}
        initialTab={viewInitialTab}
        initialStepId={viewInitialStepId}
        onDeleted={() => {
          queryClient.invalidateQueries({ queryKey: ['activities-paginated'] });
          queryClient.invalidateQueries({ queryKey: ['activities-kpis'] });
        }}
      />

      {/* v1.13.0 — Edição in-line: atividade avulsa */}
      <ActivityEditSheet
        open={!!editStandalone}
        onOpenChange={(open) => {
          if (!open) setEditStandalone(null);
        }}
        activity={editStandalone}
        onSuccess={() => {
          setEditStandalone(null);
          queryClient.invalidateQueries({ queryKey: ['activities-paginated'] });
          queryClient.invalidateQueries({ queryKey: ['activities-kpis'] });
        }}
      />

      {/* v1.13.0 — Edição in-line: step manual de cadência */}
      <ManualCadenceStepDialog
        open={!!editManualStep}
        onOpenChange={(open) => {
          if (!open) setEditManualStep(null);
        }}
        dealId={editManualStep?.deal_id ?? null}
        step={editManualStep}
        onSaved={() => {
          setEditManualStep(null);
          queryClient.invalidateQueries({ queryKey: ['activities-paginated'] });
          queryClient.invalidateQueries({ queryKey: ['activities-kpis'] });
        }}
      />

      {/* v1.5.0 — Modal individual de atividade removido. Toda navegação por clique
          no título reaproveita o DealDetailsModalPremium acima. */}
    </ListPageLayout>
  );
}

// Helpers de UI da tabela (StatusBadge, OriginCell, ActivityRowDesktop,
// ActivityCardMobile, ActivitiesSkeleton, EmptyState, ACTIVITY_ICON,
// FINAL_ACTIVITY_STATUSES, isActivityActionable) foram movidos para
// `src/components/activities/list/ActivitiesTable.tsx` (v2.1.0) para serem
// reutilizados pelo Dashboard "Minhas Atividades" sem duplicação.

// Popovers de Reagendar/Atribuir agora vivem em ActivitiesBulkEditBar (v1.1.0)

